import type { WorkspaceOut } from "@sagot/api-client";
import { consoleRoutes, webappDisplayUrl, webappPublicUrl, widgetEmbedSnippet } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, CopyButton, Icon, Input, Switch, toast, type IconName } from "@sagot/ui";
import { useState } from "react";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { readOnboarding, useSaveProgress } from "./use-onboarding";

type ChannelKey = "widget" | "webapp" | "messenger" | "mobile";
const CHANNELS: readonly { key: ChannelKey; icon: IconName; defaultOn: boolean }[] = [
  { key: "widget", icon: "globe", defaultOn: true },
  { key: "webapp", icon: "app-window", defaultOn: true },
  { key: "messenger", icon: "message-circle", defaultOn: false },
  { key: "mobile", icon: "smartphone", defaultOn: false },
];

function ChannelCard({ channel, on, onToggle, workspace }: { channel: (typeof CHANNELS)[number]; on: boolean; onToggle: (on: boolean) => void; workspace: WorkspaceOut | undefined }) {
  const { t } = useTranslation();
  const name = t(`onboarding.live.${channel.key}`);
  const site = workspace?.website?.replace(/^https?:\/\//, "");
  const body: Record<ChannelKey, string> = {
    widget: site ? t("onboarding.live.widgetBody", { site }) : t("onboarding.live.widgetBodyNoSite"),
    webapp: t("onboarding.live.webappBody"),
    messenger: t("onboarding.live.messengerBody"),
    mobile: t("onboarding.live.mobileBody"),
  };
  const titleId = `channel-${channel.key}`;
  return (
    <div className="card card-body">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-ink">
          <Icon name={channel.icon} className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-bold" id={titleId}>
            {name}
            {channel.key === "mobile" ? <Badge tone="neutral">{t("onboarding.live.planBadge")}</Badge> : null}
          </div>
          <p className="mt-0.5 text-xs text-fg-muted">{body[channel.key]}</p>
        </div>
        <Switch aria-labelledby={titleId} checked={on} onCheckedChange={onToggle} />
      </div>
      {channel.key === "messenger" && on ? (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-line bg-surface-2 px-3 py-2.5">
          <span className="text-xs text-fg-muted">{t("onboarding.live.messengerNone")}</span>
          <Button variant="secondary" size="sm" onClick={() => toast.info(t("onboarding.live.connectLater"))}>
            {t("onboarding.live.connectPage")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function EmbedCard({ site }: { site: string | undefined }) {
  const { t } = useTranslation();
  const snippet = widgetEmbedSnippet("agt_…");
  return (
    <section className="card" aria-labelledby="embed-title">
      <div className="card-header">
        <div>
          <h2 id="embed-title" className="card-title">
            {t("onboarding.live.embedTitle")}
          </h2>
          <div className="mt-0.5 text-xs text-fg-subtle">
            {t("onboarding.live.embedLead")} <span className="font-mono">&lt;/body&gt;</span> {t("onboarding.live.embedTail")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => toast.info(t("onboarding.live.emailItLater"))}>
            <Icon name="mail" />
            <span className="hidden sm:inline">{t("onboarding.live.emailIt")}</span>
          </Button>
          <CopyButton value={snippet} label={t("onboarding.live.copy")} copiedLabel={t("onboarding.live.copied")} />
        </div>
      </div>
      <div className="card-body">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable code must be keyboard-focusable (axe scrollable-region-focusable) */}
        <pre className="code whitespace-pre" tabIndex={0} aria-label={t("onboarding.live.embedTitle")}>
          {snippet}
        </pre>
        <p className="hint">
          {t("onboarding.live.agentPending")}
          {site ? (
            <>
              {" · "}
              {t("onboarding.live.embedFooter")} <span className="font-mono text-fg-muted">{site}</span>
            </>
          ) : null}
        </p>
      </div>
    </section>
  );
}

function WebAppCard({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const link = webappPublicUrl(slug);
  return (
    <section className="card card-body" aria-labelledby="app-link-label">
      <label className="label" id="app-link-label" htmlFor="app-link">
        {t("onboarding.live.webappLink")}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input id="app-link" className="font-mono text-[13px]" value={link} readOnly />
        <div className="flex gap-2">
          <CopyButton value={link} label={t("onboarding.live.copy")} copiedLabel={t("onboarding.live.copied")} />
          <Button asChild variant="secondary">
            <a href={link} target="_blank" rel="noreferrer">
              <Icon name="external-link" />
              {t("onboarding.live.open")}
            </a>
          </Button>
        </div>
      </div>
      <p className="hint">
        {t("onboarding.live.fallbackDomain")} <span className="font-mono text-fg-muted">{webappDisplayUrl(slug)}</span>
      </p>
    </section>
  );
}

interface StepGoLiveProps {
  workspace: WorkspaceOut | undefined;
  onBack: () => void;
  onFinish: () => Promise<void>;
}

/** Step 5: channel switches (saved to the setup data), embed snippet (DEC-011) and web app link. */
export function StepGoLive({ workspace, onBack, onFinish }: StepGoLiveProps) {
  const { t } = useTranslation();
  const save = useSaveProgress(workspace);
  const message = useErrorMessage();
  const saved = readOnboarding(workspace).data.channels ?? {};
  const [channels, setChannels] = useState<Record<ChannelKey, boolean>>(() => Object.fromEntries(CHANNELS.map((c) => [c.key, saved[c.key] ?? c.defaultOn])) as Record<ChannelKey, boolean>);
  const [error, setError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const toggle = (key: ChannelKey, on: boolean) => {
    setChannels({ ...channels, [key]: on });
    const channel = t(`onboarding.live.${key}`);
    toast.success(on ? t("onboarding.live.enabled", { channel }) : t("onboarding.live.disabled", { channel }));
  };
  const finish = async () => {
    setFinishing(true);
    try {
      await save(5, { channels, completed: true });
      await onFinish();
    } catch (caught) {
      setError(message(caught));
      setFinishing(false);
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("onboarding.live.heading")}</h1>
        <p className="mt-1 text-fg-muted">{t("onboarding.live.subtitle")}</p>
      </div>
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {CHANNELS.map((channel) => (
          <ChannelCard key={channel.key} channel={channel} on={channels[channel.key]} onToggle={(on) => { toggle(channel.key, on); }} workspace={workspace} />
        ))}
      </div>
      {channels.widget ? <EmbedCard site={workspace?.website?.replace(/^https?:\/\//, "")} /> : null}
      {channels.webapp && workspace ? <WebAppCard slug={workspace.slug} /> : null}
      <div className="flex flex-col-reverse gap-3 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="ghost" className="self-start" onClick={onBack}>
          <Icon name="arrow-left" />
          {t("onboarding.back")}
        </Button>
        <div className="flex flex-col gap-2 sm:flex-row">
          {workspace ? (
            <Button asChild variant="secondary">
              <AppLink href={consoleRoutes.team(workspace.slug)}>
                <Icon name="user-plus" />
                {t("onboarding.live.inviteTeam")}
              </AppLink>
            </Button>
          ) : null}
          <Button loading={finishing} onClick={() => void finish()}>
            <Icon name="check" />
            {t("onboarding.live.finish")}
          </Button>
        </div>
      </div>
    </div>
  );
}
